/**
 * Utility functions for validating test answers
 */

/**
 * Checks if the selected option matches the correct answer
 * Supports multiple formats:
 * 1. Direct equality (after trimming)
 * 2. Multiple possible answers separated by ',', ';', or '/'
 * 3. Numerical equivalence (e.g., "1/4" and "0.25" are considered equal)
 * 
 * @param selectedOption - The user's selected option
 * @param correctAnswer - The correct answer(s) from the question data
 * @returns boolean indicating if the answer is correct
 */
export function isAnswerCorrect(selectedOption: string, correctAnswer: string): boolean {
  if (!selectedOption || !correctAnswer) {
    return false;
  }

  // Trim both values
  const trimmedSelected = selectedOption.trim();
  const trimmedCorrect = correctAnswer.trim();

  // Case 1: Direct equality after trimming
  if (trimmedSelected === trimmedCorrect) {
    return true;
  }

  // Case 2: Check if correctAnswer contains multiple possible answers
  // Split by semicolon, comma, slash, or the word "or" (with optional spaces)
  const possibleAnswers = trimmedCorrect
    .split(/[,;\/]|\s+or\s+/i)
    .map(answer => answer.trim())
    .filter(answer => answer.length > 0); // Filter out empty answers
  
  // Check if selected option matches any of the possible answers
  if (possibleAnswers.includes(trimmedSelected)) {
    return true;
  }

  // Case 3: Check for numerical equivalence
  try {
    // Try to convert both to numbers for comparison
    const selectedNumber = parseFloat(trimmedSelected.replace(',', '.'));
    
    // Check if selected is a valid number
    if (!isNaN(selectedNumber)) {
      // Check each possible answer for numerical equivalence
      for (const answer of possibleAnswers) {
        // Handle fractions like "1/4"
        if (answer.includes('/')) {
          const [numerator, denominator] = answer.split('/').map(n => parseFloat(n.trim()));
          if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
            const fraction = numerator / denominator;
            // Use a small epsilon for floating point comparison
            if (Math.abs(fraction - selectedNumber) < 0.0001) {
              return true;
            }
          }
        } else {
          // Regular number comparison
          const correctNumber = parseFloat(answer.replace(',', '.'));
          if (!isNaN(correctNumber) && Math.abs(correctNumber - selectedNumber) < 0.0001) {
            return true;
          }
        }
      }
    }
  } catch (error) {
    // If any error in parsing, fall back to string comparison
    console.error('Error comparing numerical answers:', error);
  }

  return false;
}
